import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';

interface LeftSidebarContextType {
	isOpen: boolean;
	openSidebar: () => void;
	closeSidebar: () => void;
	toggleSidebar: () => void;
}

const LeftSidebarContext = createContext<LeftSidebarContextType | undefined>(
	undefined,
);

export const useLeftSidebarContext = () => {
	const context = useContext(LeftSidebarContext);
	if (!context) {
		throw new Error(
			'useLeftSidebarContext must be used within a LeftSidebarProvider',
		);
	}
	return context;
};

interface LeftSidebarProviderProps {
	children: ReactNode;
}

export const LeftSidebarProvider: React.FC<LeftSidebarProviderProps> = ({
	children,
}) => {
	const isMobile = () => window.innerWidth < 768;
	// On mobile: closed by default, on desktop: open by default
	const [isOpen, setIsOpen] = useState(!isMobile());

	useEffect(() => {
		const handleResize = () => {
			// Only auto-close on mobile
			// Don't force open on desktop - let MainLayout control sidebar based on route
			if (isMobile()) {
				setIsOpen(false);
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const openSidebar = () => {
		setIsOpen(true);
	};

	const closeSidebar = () => {
		setIsOpen(false);
	};

	const toggleSidebar = () => {
		setIsOpen((prev) => !prev);
	};

	const contextValue = {
		isOpen,
		openSidebar,
		closeSidebar,
		toggleSidebar,
	};

	return (
		<LeftSidebarContext.Provider value={contextValue}>
			{children}
		</LeftSidebarContext.Provider>
	);
};

export default LeftSidebarContext;
